import React, { useState } from 'react';
import { InvitationDesign } from '../../../_types/invitation-design-v2';

interface ColorsTabProps {
  design: InvitationDesign;
  onThemeUpdate: (updates: any) => void;
  onTemplateUpdate: (updates: any) => void;
}

const COLOR_PRESETS = [
  {
    id: 'classic-gold',
    name: 'Classic Gold',
    colors: {
      primaryColor: '#D4AF37',
      secondaryColor: '#8B7355',
      textColor: '#3A3A3A',
      accentColor: '#F5E6D3',
      backgroundColor: '#FFFFF5'
    }
  },
  {
    id: 'modern-navy',
    name: 'Modern Navy',
    colors: {
      primaryColor: '#1E3A8A',
      secondaryColor: '#3B82F6',
      textColor: '#1F2937',
      accentColor: '#DBEAFE',
      backgroundColor: '#FFFFFF'
    }
  },
  {
    id: 'romantic-blush',
    name: 'Romantic Blush',
    colors: {
      primaryColor: '#F472B6',
      secondaryColor: '#EC4899',
      textColor: '#374151',
      accentColor: '#FCE7F3',
      backgroundColor: '#FEF7FF'
    }
  },
  {
    id: 'sage-green',
    name: 'Sage Green',
    colors: {
      primaryColor: '#6B7280',
      secondaryColor: '#10B981',
      textColor: '#374151',
      accentColor: '#D1FAE5',
      backgroundColor: '#F9FAFB'
    }
  },
  {
    id: 'burgundy-wine',
    name: 'Burgundy Wine',
    colors: {
      primaryColor: '#7C2D12',
      secondaryColor: '#DC2626',
      textColor: '#1F2937',
      accentColor: '#FEE2E2',
      backgroundColor: '#FFFBEB'
    }
  },
  {
    id: 'dusty-rose',
    name: 'Dusty Rose',
    colors: {
      primaryColor: '#BE185D',
      secondaryColor: '#F59E0B',
      textColor: '#374151',
      accentColor: '#FDF2F8',
      backgroundColor: '#FFFBEB'
    }
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    colors: {
      primaryColor: '#14532D',
      secondaryColor: '#059669',
      textColor: '#1F2937',
      accentColor: '#ECFDF5',
      backgroundColor: '#F9FAFB'
    }
  },
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    colors: {
      primaryColor: '#6B21A8',
      secondaryColor: '#A855F7',
      textColor: '#1F2937',
      accentColor: '#F3E8FF',
      backgroundColor: '#FEFEFE'
    }
  },
  {
    id: 'coral-sunset',
    name: 'Coral Sunset',
    colors: {
      primaryColor: '#EA580C',
      secondaryColor: '#F97316',
      textColor: '#1F2937',
      accentColor: '#FED7AA',
      backgroundColor: '#FFFBEB'
    }
  },
  {
    id: 'midnight-blue',
    name: 'Midnight Blue',
    colors: {
      primaryColor: '#0F172A',
      secondaryColor: '#475569',
      textColor: '#1E293B',
      accentColor: '#F1F5F9',
      backgroundColor: '#FFFFFF'
    }
  },
  {
    id: 'lavender-dream',
    name: 'Lavender Dream',
    colors: {
      primaryColor: '#7C3AED',
      secondaryColor: '#A78BFA',
      textColor: '#374151',
      accentColor: '#EDE9FE',
      backgroundColor: '#FEFEFE'
    }
  },
  {
    id: 'autumn-spice',
    name: 'Autumn Spice',
    colors: {
      primaryColor: '#92400E',
      secondaryColor: '#D97706',
      textColor: '#1F2937',
      accentColor: '#FEF3C7',
      backgroundColor: '#FFFBEB'
    }
  }
];

const ColorsTab: React.FC<ColorsTabProps> = ({
  design,
  onThemeUpdate,
  onTemplateUpdate,
}) => {
  const [showColorPresets, setShowColorPresets] = useState(false);

  return (
    <div className="space-y-6">
      {/* Color Presets */}
      <div>
        <button
          onClick={() => setShowColorPresets(!showColorPresets)}
          className="w-full flex items-center justify-between p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors mb-2"
        >
          <span className="text-sm font-medium text-gray-700">Color Presets</span>
          <svg 
            className={`w-4 h-4 transform transition-transform ${showColorPresets ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showColorPresets && (
          <div className="space-y-2">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset.id}
                onClick={() => {
                  onThemeUpdate(preset.colors);
                  onTemplateUpdate({
                    background: {
                      type: 'solid',
                      value: preset.colors.backgroundColor
                    }
                  });
                }}
                className="w-full p-3 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors flex items-center gap-3"
              >
                {/* Color Squares */}
                <div className="flex gap-1">
                  <div 
                    className="w-4 h-4 rounded border border-gray-300" 
                    style={{ backgroundColor: preset.colors.primaryColor }}
                    title="Primary"
                  />
                  <div 
                    className="w-4 h-4 rounded border border-gray-300" 
                    style={{ backgroundColor: preset.colors.secondaryColor }}
                    title="Secondary"
                  />
                  <div 
                    className="w-4 h-4 rounded border border-gray-300" 
                    style={{ backgroundColor: preset.colors.textColor }}
                    title="Text"
                  />
                  <div 
                    className="w-4 h-4 rounded border border-gray-300" 
                    style={{ backgroundColor: preset.colors.accentColor }}
                    title="Accent"
                  />
                  <div 
                    className="w-4 h-4 rounded border border-gray-300" 
                    style={{ backgroundColor: preset.colors.backgroundColor }}
                    title="Background"
                  />
                </div>
                <span className="text-sm font-medium text-gray-700">{preset.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Actual Background Color */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Invitation Background
        </label>
        <div className="flex gap-2">
          <input
            type="color"
            value={design.template.background.type === 'solid' ? design.template.background.value : '#ffffff'}
            onChange={(e) => onTemplateUpdate({
              background: {
                type: 'solid',
                value: e.target.value
              }
            })}
            className="w-12 h-12 rounded cursor-pointer"
          />
          <input
            type="text"
            value={design.template.background.type === 'solid' ? design.template.background.value : '#ffffff'}
            onChange={(e) => onTemplateUpdate({
              background: {
                type: 'solid',
                value: e.target.value
              }
            })}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>
      
      {/* Theme Colors */}
      {Object.entries(design.template.theme).filter(([key]) => key.includes('Color') && key !== 'backgroundColor').map(([key, value]) => {
        const label = key.replace(/([A-Z])/g, ' $1').replace('Color', '').trim();
        return (
          <div key={key}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {label.charAt(0).toUpperCase() + label.slice(1)}
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={value as string}
                onChange={(e) => onThemeUpdate({ [key]: e.target.value })}
                className="w-12 h-12 rounded cursor-pointer"
              />
              <input
                type="text"
                value={value as string}
                onChange={(e) => onThemeUpdate({ [key]: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ColorsTab;